import { Loader2, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useGameState } from "@/components/game-state-context";
import {
  EmptyState,
  type EmptyStateVariant,
} from "@/components/maps/empty-states";
import { EntityPanel } from "@/components/maps/entity-panel";
import { MapGraph } from "@/components/maps/map-graph";
import { SpatialGrid } from "@/components/maps/spatial-grid";
import { Button } from "@/components/ui/button";
import {
  MapAuthError,
  MapForbiddenError,
  MapNotFoundError,
  useMapView,
} from "@/hooks/use-map-view";
import type { MapViewEntity, MapViewNode } from "@/lib/core/schemas/map-view";
import type { Point } from "@/lib/core/schemas/spatial";
import logger from "@/lib/logger";
import type { PendingChoiceView } from "@/lib/systems/pending-choices/read-view";
import { cn } from "@/lib/utils";
import { callMcpTool } from "@/src/api/mcp";

const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window === "undefined"
      ? false
      : window.matchMedia("(min-width: 768px)").matches
  );
  useEffect(() => {
    const mql = window.matchMedia("(min-width: 768px)");
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);
  return isDesktop;
};

const errorToVariant = (err: Error): EmptyStateVariant => {
  if (err instanceof MapAuthError) {
    return "auth";
  }
  if (err instanceof MapForbiddenError) {
    return "forbidden";
  }
  if (err instanceof MapNotFoundError) {
    return "not-found";
  }
  const msg = err.message;
  if (msg.includes("Failed to fetch") || msg.includes("NetworkError")) {
    return "offline";
  }
  return "kernel-down";
};

export function MapsTab({
  campaignId,
  onPendingChoiceResolved,
}: {
  campaignId: string;
  onPendingChoiceResolved?: () => void;
}) {
  const { data, error, isLoading, refresh } = useMapView(campaignId);
  const { invalidate, pendingChoices } = useGameState();
  const [viewMode, setViewMode] = useState<"graph" | "spatial">("graph");
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  const [isResolvingDestination, setIsResolvingDestination] = useState(false);
  const [destinationError, setDestinationError] = useState<string | null>(null);
  const isDesktop = useIsDesktop();

  const selectedNode: MapViewNode | null =
    data?.nodes.find((n) => n.id === selectedNodeId) ?? null;
  const selectedEntity: MapViewEntity | null =
    data?.entities.find((e) => e.id === selectedEntityId) ?? null;
  const selectedEntityLocatedInName: string | null = selectedEntity?.locatedIn
    ? (data?.entities.find((e) => e.id === selectedEntity.locatedIn)?.name ??
      null)
    : null;
  const pendingDestinationChoices = useMemo(
    () =>
      pendingChoices.filter(
        (choice) =>
          choice.status === "pending" && choice.kind === "point_destination"
      ),
    [pendingChoices]
  );
  const activePointDestinationChoice: PendingChoiceView | null = useMemo(() => {
    return pendingDestinationChoices[0] ?? null;
  }, [pendingDestinationChoices]);

  useEffect(() => {
    if (
      viewMode !== "spatial" ||
      selectedZoneId !== null ||
      !activePointDestinationChoice ||
      !data
    ) {
      return;
    }
    const actor = data.entities.find(
      (entity) => entity.id === activePointDestinationChoice.actorId
    );
    if (actor?.locatedIn) {
      setSelectedZoneId(actor.locatedIn);
    }
  }, [activePointDestinationChoice, data, selectedZoneId, viewMode]);

  // View-mode switching with state-clearing rule: switching clears the
  // inactive view's selection so a stale Sheet can't re-open after a
  // round-trip toggle.
  const switchToSpatial = useCallback(() => {
    setSelectedNodeId(null);
    setViewMode("spatial");
  }, []);
  const switchToGraph = useCallback(() => {
    setSelectedEntityId(null);
    setDestinationError(null);
    setViewMode("graph");
  }, []);

  const handleNodeClick = useCallback(
    (nodeId: string) => setSelectedNodeId(nodeId),
    []
  );
  const handleSelectEntity = useCallback(
    (entityId: string) => setSelectedEntityId(entityId),
    []
  );
  const handlePanelClose = useCallback(() => {
    setSelectedNodeId(null);
    setSelectedEntityId(null);
  }, []);
  const handleViewSpatialGrid = useCallback((zoneId: string) => {
    setSelectedZoneId(zoneId);
    setSelectedNodeId(null);
    setViewMode("spatial");
  }, []);
  const handleSpatialSwitchView = useCallback(
    (mode: "graph" | "spatial") => {
      if (mode === "graph") {
        switchToGraph();
      } else {
        switchToSpatial();
      }
    },
    [switchToGraph, switchToSpatial]
  );
  const handleChooseDestination = useCallback(
    async (destination: Point) => {
      if (!activePointDestinationChoice || isResolvingDestination) {
        return;
      }
      setIsResolvingDestination(true);
      setDestinationError(null);
      try {
        await callMcpTool(
          "resolve_pending_choice",
          {
            choiceId: activePointDestinationChoice.entityId,
            decision: "resolve",
            destination,
          },
          campaignId
        );
        invalidate(["pending-choices"]);
        await refresh();
        onPendingChoiceResolved?.();
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to resolve destination";
        logger.error(
          { err, choiceId: activePointDestinationChoice.entityId },
          "Failed to resolve pending destination choice"
        );
        setDestinationError(message);
      } finally {
        setIsResolvingDestination(false);
      }
    },
    [
      activePointDestinationChoice,
      campaignId,
      invalidate,
      isResolvingDestination,
      onPendingChoiceResolved,
      refresh,
    ]
  );

  const variant: EmptyStateVariant | null = useMemo(() => {
    // show spinner instead of empty state on first load
    if (isLoading && !data) {
      return null;
    }
    if (error && data === null) {
      return errorToVariant(error);
    }
    if (data && data.nodes.length === 0) {
      return "no-locations";
    }
    return null;
  }, [isLoading, data, error]);

  return (
    <div className="flex h-full flex-col bg-background text-foreground">
      <header className="flex h-12 items-center justify-between border-border border-b px-4">
        <div className="flex items-center gap-1 rounded-md bg-secondary p-1">
          <button
            className={cn(
              "rounded px-3 py-1 font-medium text-xs",
              viewMode === "graph"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground"
            )}
            onClick={switchToGraph}
            type="button"
          >
            Graph
          </button>
          <button
            className={cn(
              "rounded px-3 py-1 font-medium text-xs",
              viewMode === "spatial"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground"
            )}
            onClick={switchToSpatial}
            type="button"
          >
            Spatial
          </button>
        </div>
        <Button
          aria-label="Refresh map"
          onClick={refresh}
          size="sm"
          type="button"
          variant="outline"
        >
          {isLoading ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <RefreshCw className="h-3 w-3" />
          )}
          <span className="ml-1 hidden text-xs md:inline">Refresh</span>
        </Button>
      </header>

      <main className="relative flex-1 overflow-hidden">
        {isLoading && !data && (
          <div className="flex h-full items-center justify-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm italic">Reading kernel state…</span>
          </div>
        )}
        {variant && <EmptyState onRetry={refresh} variant={variant} />}
        {data && viewMode === "graph" && data.nodes.length > 0 && !variant && (
          <div
            className={cn(
              "h-full w-full",
              error && "opacity-60 transition-opacity"
            )}
          >
            <MapGraph mapView={data} onNodeClick={handleNodeClick} />
          </div>
        )}
        {data && viewMode === "spatial" && !variant && (
          <div
            className={cn(
              "relative h-full w-full",
              error && "opacity-60 transition-opacity"
            )}
          >
            {activePointDestinationChoice && (
              <output
                aria-live="polite"
                className="pointer-events-auto absolute top-2 right-2 left-2 z-10 rounded-md border border-primary/30 bg-background/95 px-3 py-2 shadow-sm backdrop-blur"
              >
                <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
                  <span className="font-medium text-sm">
                    {activePointDestinationChoice.source.label}
                  </span>
                  {activePointDestinationChoice.constraints?.maxDistanceFt !==
                    undefined && (
                    <span className="text-muted-foreground text-xs">
                      {activePointDestinationChoice.constraints?.maxDistanceFt}{" "}
                      ft
                    </span>
                  )}
                </div>
                <div className="mt-1 break-words text-sm">
                  {activePointDestinationChoice.prompt}
                </div>
                {destinationError && (
                  <div
                    className="mt-1 break-words text-destructive text-xs"
                    role="alert"
                  >
                    {destinationError}
                  </div>
                )}
              </output>
            )}
            <SpatialGrid
              activeDestinationPick={
                activePointDestinationChoice
                  ? {
                      z:
                        activePointDestinationChoice.constraints?.origin?.z ??
                        0,
                    }
                  : null
              }
              isResolvingDestination={isResolvingDestination}
              mapView={data}
              onChooseDestination={handleChooseDestination}
              onSelectEntity={handleSelectEntity}
              onSwitchView={handleSpatialSwitchView}
              zoneId={selectedZoneId}
            />
          </div>
        )}
        {error && data && !variant && (
          <div className="-translate-x-1/2 absolute bottom-2 left-1/2 rounded border border-destructive/30 bg-destructive/10 px-3 py-1 text-destructive text-xs">
            Refresh failed — showing data from{" "}
            {new Date(data.capturedAt).toLocaleTimeString()}
          </div>
        )}
        <EntityPanel
          entity={viewMode === "spatial" ? selectedEntity : null}
          locatedInName={
            viewMode === "spatial" ? selectedEntityLocatedInName : null
          }
          node={viewMode === "graph" ? selectedNode : null}
          onClose={handlePanelClose}
          onViewSpatialGrid={handleViewSpatialGrid}
          rawState={viewMode === "graph" ? selectedNode : selectedEntity}
          side={isDesktop ? "right" : "bottom"}
        />
      </main>

      {data && (
        <footer className="flex h-6 items-center justify-center border-border border-t text-[10px] text-muted-foreground">
          Captured {new Date(data.capturedAt).toLocaleTimeString()} ·{" "}
          {data.nodes.length} nodes · {data.edges.length} edges
        </footer>
      )}
    </div>
  );
}
